import merge from 'lodash/merge';
import queryString from 'qs';
import { handleOpmlError } from '../../errors/OpmlError';
import { graphQlClient } from '../../graphql';
import { transformNowPlaying } from '../../transforms';
import transformOpmlResponseHead from '../../transforms/transformOpmlResponseHead';
import tuneInRequest from '../../tuneInRequest';
import format from 'string-format';
export function profile(config, apiContext, applyReauth) {
    const meEndpoint = `${config.domains.api}/profiles/me`;
    const meFollowsEndpoint = `${meEndpoint}/follows`;
    const apiEndpoint = `${config.domains.api}/profiles`;
    const feedEndpoint = `${config.domains.feed}/profiles`;
    const v3ApiEndpoint = format('{api}/profiles/{{}}/contents', config.domains);
    const proxyEndpoint = format('{local}/proxy', config.domains);
    const songLookupEndpoint = `${config.domains.opml}/Songlookup.ashx`;
    function get(guideId) {
        return tuneInRequest({
            method: 'get',
            url: `${apiEndpoint}/${guideId}`,
            noAuthHeader: true,
        }, apiContext);
    }
    function getV3(guideId, params) {
        return tuneInRequest({
            method: 'get',
            url: format(v3ApiEndpoint, guideId),
            params,
        }, apiContext);
    }
    function search(term, params) {
        return tuneInRequest({
            method: 'get',
            url: apiEndpoint,
            params: merge({ fullTextSearch: true, query: term }, params),
        }, apiContext);
    }
    function prepopulateSearch(term, params) {
        return tuneInRequest({
            method: 'get',
            url: apiEndpoint,
            params: merge({ isPrepopulateSearch: true, query: term }, params),
        }, apiContext);
    }
    async function nowPlaying(guideId, params, retries) {
        const nowPlayingResponse = await tuneInRequest({
            method: 'get',
            url: `${feedEndpoint}/${guideId}/nowPlaying`,
            transformResponse: transformNowPlaying,
            params,
            retries,
        }, apiContext);
        return nowPlayingResponse?.data;
    }
    function proxyNowPlaying(guideId, params) {
        return tuneInRequest({
            method: 'get',
            url: `${proxyEndpoint}/${guideId}/nowPlaying`,
            transformResponse: transformNowPlaying,
            params,
        }, apiContext);
    }
    async function getStreamArtworkUrl(artist, title) {
        const response = await tuneInRequest({
            method: 'get',
            url: songLookupEndpoint,
            params: { artist, title },
            transformResponse: transformOpmlResponseHead,
        }, apiContext).then(handleOpmlError);
        return response?.data?.body?.[0]?.artistArtUrl;
    }
    function topics(programGuideId, params) {
        return tuneInRequest({
            method: 'get',
            url: `${format(v3ApiEndpoint, programGuideId)}/topics`,
            params,
        }, apiContext);
    }
    function userByName(userIdentifier) {
        return tuneInRequest({
            method: 'get',
            url: `${apiEndpoint}/${userIdentifier}/follows`,
            params: {
                aliastype: 'username',
            },
        }, apiContext);
    }
    function userById(userIdentifier, params) {
        return tuneInRequest({
            method: 'get',
            url: `${apiEndpoint}/${userIdentifier}/follows`,
            params,
        }, apiContext);
    }
    function favorites(username) {
        return tuneInRequest({
            method: 'get',
            url: meFollowsEndpoint,
            params: {
                username,
            },
        }, apiContext);
    }
    function favoritesFolder(username, folderId) {
        return tuneInRequest({
            method: 'get',
            url: meFollowsEndpoint,
            params: {
                username,
                folderId,
                filter: 'favorites',
            },
        }, apiContext);
    }
    function getMeWithFavorites() {
        return tuneInRequest({
            method: 'get',
            url: meFollowsEndpoint,
        }, apiContext);
    }
    function getMe(useLegacyApi = false) {
        if (useLegacyApi) {
            return tuneInRequest({
                method: 'get',
                url: meEndpoint,
            }, apiContext);
        }
        return graphQlClient.sdk.getUser({
            device: { partnerId: apiContext.partnerId, serial: config.serial },
        });
    }
    function saveMe({ name, email, isFollowingListPublic }) {
        return tuneInRequest({
            method: 'post',
            url: meEndpoint,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            data: queryString.stringify({ name, email, isFollowingListPublic }),
        }, apiContext);
    }
    function getMeDevices() {
        return tuneInRequest({
            method: 'get',
            url: `${meEndpoint}/devices/`,
        }, apiContext);
    }
    function addMeDevice(registrationCode) {
        return tuneInRequest({
            method: 'post',
            url: `${meEndpoint}/devices/`,
            data: { registrationCode },
        }, apiContext);
    }
    function deleteMeDevice(id) {
        return tuneInRequest({
            method: 'delete',
            url: `${meEndpoint}/devices/${id}/`,
        }, apiContext);
    }
    function verifyDevicePin(pinCode) {
        return tuneInRequest({
            method: 'get',
            url: `${meEndpoint}/deviceGrantSessions/byUserCode/${pinCode}`,
        }, apiContext);
    }
    function grantAuthToDevice(pinCode) {
        return tuneInRequest({
            method: 'put',
            url: `${meEndpoint}/deviceGrantSessions/byUserCode/${pinCode}/status/granted`,
        }, apiContext);
    }
    const getMeConsents = (userId) => graphQlClient.sdk.getUserConsents({ userId });
    const addMeConsent = (consent) => graphQlClient.sdk.addUserConsent({ consent });
    return {
        get,
        getV3: applyReauth(getV3),
        search: applyReauth(search),
        prepopulateSearch: applyReauth(prepopulateSearch),
        nowPlaying: applyReauth(nowPlaying),
        proxyNowPlaying: applyReauth(proxyNowPlaying),
        getStreamArtworkUrl: applyReauth(getStreamArtworkUrl),
        topics: applyReauth(topics),
        userByName: applyReauth(userByName),
        userById: applyReauth(userById),
        favorites: applyReauth(favorites),
        favoritesFolder: applyReauth(favoritesFolder),
        getMeWithFavorites: applyReauth(getMeWithFavorites),
        getMe: applyReauth(getMe),
        saveMe: applyReauth(saveMe),
        getMeDevices: applyReauth(getMeDevices),
        addMeDevice: applyReauth(addMeDevice),
        deleteMeDevice: applyReauth(deleteMeDevice),
        verifyDevicePin,
        grantAuthToDevice: applyReauth(grantAuthToDevice),
        getMeConsents: applyReauth(getMeConsents),
        addMeConsent: applyReauth(addMeConsent),
    };
}
